import { AssistantAvatar } from "@/components/Chat/header/AssistantSelector";
import AssistantForm from "@/components/forms/AssistantForm";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { supabase } from "@/lib/supabase";
import useAssistantStore from "@/store/assistantStore";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Plus, Save, Settings, Unlink, X } from "lucide-react";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";

// Assistant card component with simplified animations
function AssistantCard({
  assistant,
  isWorkspaceAssistant,
  isExpanded,
  onSettingsClick,
  onAssistantAction,
  onSavingStateChange,
  isTransitioning = false,
  transitionType = null,
}) {
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveFunction, setSaveFunction] = useState(null);

  const handleSavingStateChange = (
    saving,
    success,
    hasPendingChanges,
    saveFn
  ) => {
    setIsSaving(saving);
    setHasUnsavedChanges(hasPendingChanges);
    if (saveFn) {
      setSaveFunction(() => saveFn);
    }
    onSavingStateChange?.(saving, success, hasPendingChanges, saveFn);
  };

  const handleSave = async () => {
    if (saveFunction) {
      await saveFunction();
    }
  };

  const getActionButtonContent = () => {
    if (isTransitioning) {
      return <Loader2 className="h-4 w-4 animate-spin" />;
    }

    return isWorkspaceAssistant ? (
      <Unlink className="h-4 w-4" />
    ) : (
      <Plus className="h-4 w-4" />
    );
  };

  return (
    <TooltipProvider>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
        className={`bg-gray-50 dark:bg-zinc-800/40 rounded-2xl hover:bg-gray-100 dark:hover:bg-zinc-800/60 transition-colors border border-gray-200 dark:border-zinc-700 group overflow-hidden ${
          isTransitioning ? "opacity-60" : ""
        }`}
      >
        <div className="flex justify-between items-center p-3">
          <div className="flex items-center gap-3">
            <AssistantAvatar
              assistant={assistant}
              className={`h-8 w-8 ring-2 transition-all ${
                isWorkspaceAssistant
                  ? "ring-violet-200 dark:ring-violet-500/30"
                  : "ring-gray-200 dark:ring-zinc-700"
              }`}
            />

            <div>
              <div className="text-gray-900 dark:text-zinc-200 font-medium">
                {assistant?.name || "Unnamed Assistant"}
              </div>
              <div className="text-gray-500 dark:text-zinc-500 text-xs flex items-center gap-2">
                {assistant?.model || "gpt-4.1-mini"}
                {isTransitioning && (
                  <span className="text-blue-600 dark:text-blue-400 text-xs font-medium">
                    {transitionType === "attach"
                      ? "Attaching..."
                      : "Detaching..."}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Save button - only show when there are unsaved changes */}
            <AnimatePresence>
              {hasUnsavedChanges && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.2 }}
                >
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 bg-transparent border border-purple-200/50 dark:border-purple-500/30 text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 hover:bg-purple-50/50 dark:hover:bg-purple-900/20 transition-all rounded-xl"
                    onClick={handleSave}
                    disabled={isSaving || !saveFunction || isTransitioning}
                  >
                    {isSaving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`h-8 w-8 bg-transparent border border-white/20 dark:border-white/10 text-gray-600 dark:text-zinc-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-all rounded-xl ${
                    isExpanded
                      ? "bg-blue-50/50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                      : ""
                  }`}
                  onClick={() => onSettingsClick(assistant)}
                  disabled={isTransitioning}
                >
                  <Settings className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{isExpanded ? "Close settings" : "Configure assistant"}</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`h-8 w-8 bg-transparent border border-white/20 dark:border-white/10 text-gray-600 dark:text-zinc-400 transition-all rounded-xl ${
                    isWorkspaceAssistant
                      ? "hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50/50 dark:hover:bg-red-900/20"
                      : "hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50/50 dark:hover:bg-green-900/20"
                  } ${isTransitioning ? "cursor-not-allowed" : ""}`}
                  onClick={() =>
                    onAssistantAction(
                      assistant.id,
                      isWorkspaceAssistant ? "detach" : "attach"
                    )
                  }
                  disabled={isTransitioning}
                >
                  {getActionButtonContent()}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {isTransitioning
                    ? transitionType === "attach"
                      ? "Attaching assistant..."
                      : "Detaching assistant..."
                    : isWorkspaceAssistant
                      ? "Remove from project"
                      : "Add to project"}
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="border-t border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900/50 overflow-hidden"
            >
              <div className="p-4">
                <div className="max-h-[400px] overflow-y-auto">
                  <AssistantForm
                    assistant={assistant}
                    onSavingStateChange={handleSavingStateChange}
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </TooltipProvider>
  );
}

export function AssistantManagementDialog({
  open,
  onOpenChange,
  workspaceId,
  onAssistantAction,
  onSettingsClick,
}) {
  const [expandedAssistantId, setExpandedAssistantId] = useState(null);
  const [workspaceAssistants, setWorkspaceAssistants] = useState([]);
  const [availableAssistants, setAvailableAssistants] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [transitioningAssistants, setTransitioningAssistants] = useState(
    new Set()
  );
  const [transitionTypes, setTransitionTypes] = useState(new Map());

  const {
    assistants,
    initializeAssistants,
    isLoading: assistantsLoading,
  } = useAssistantStore();

  // Fetch assistants when dialog opens
  useEffect(() => {
    const fetchAssistants = async () => {
      if (!open || !workspaceId) return;

      setIsLoading(true);

      try {
        // Initialize assistants if not already done
        if (!assistants || assistants.length === 0) {
          await initializeAssistants();
        }

        // Fetch workspace data to get workspace_assistants array
        const { data: workspace, error: workspaceError } = await supabase
          .from("workspaces")
          .select("workspace_assistants")
          .eq("id", workspaceId)
          .single();

        if (workspaceError) {
          console.error("Error fetching workspace:", workspaceError);
          return;
        }

        const workspaceAssistantIds = workspace?.workspace_assistants || [];

        // Fetch workspace assistants
        if (workspaceAssistantIds.length > 0) {
          const { data: fetchedWorkspaceAssistants, error } = await supabase
            .from("assistants")
            .select("*")
            .in("id", workspaceAssistantIds);

          if (error) {
            console.error("Error fetching workspace assistants:", error);
            setWorkspaceAssistants([]);
          } else {
            const assistantsWithTools = (fetchedWorkspaceAssistants || []).map(
              (assistant) =>
                useAssistantStore
                  .getState()
                  .reconstructAssistantTools(assistant)
            );
            setWorkspaceAssistants(assistantsWithTools);
          }
        } else {
          setWorkspaceAssistants([]);
        }

        // Get available assistants (all assistants minus workspace assistants)
        const currentAssistants = useAssistantStore.getState().assistants;
        const filtered = currentAssistants.filter((assistant) => {
          return !workspaceAssistantIds.includes(assistant.id);
        });

        setAvailableAssistants(filtered);
      } catch (error) {
        console.error("Error fetching assistants:", error);
        setWorkspaceAssistants([]);
        setAvailableAssistants([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAssistants();
  }, [open, workspaceId, assistants, initializeAssistants]);

  const handleSettingsClick = (assistant) => {
    if (expandedAssistantId === assistant.id) {
      setExpandedAssistantId(null);
    } else {
      setExpandedAssistantId(assistant.id);
    }
    onSettingsClick?.(assistant);
  };

  const handleAssistantAction = useCallback(
    async (assistantId, action) => {
      // Find the assistant in either list
      const assistant = [...workspaceAssistants, ...availableAssistants].find(
        (a) => a.id === assistantId
      );

      if (!assistant) {
        console.error("❌ Assistant not found in dialog state:", {
          assistantId,
          workspaceCount: workspaceAssistants.length,
          availableCount: availableAssistants.length,
        });
        return;
      }

      // Set transitioning state
      setTransitioningAssistants((prev) => new Set([...prev, assistantId]));
      setTransitionTypes((prev) => new Map([...prev, [assistantId, action]]));

      // Optimistically update the UI
      if (action === "attach") {
        setAvailableAssistants((prev) =>
          prev.filter((a) => a.id !== assistantId)
        );
        setWorkspaceAssistants((prev) => [...prev, assistant]);
      } else if (action === "detach") {
        setWorkspaceAssistants((prev) =>
          prev.filter((a) => a.id !== assistantId)
        );
        setAvailableAssistants((prev) => [...prev, assistant]);
      }

      try {
        // Call the actual action
        await onAssistantAction(assistantId, action);

        // Success - remove from transitioning after a short delay
        setTimeout(() => {
          setTransitioningAssistants((prev) => {
            const newSet = new Set(prev);
            newSet.delete(assistantId);
            return newSet;
          });
          setTransitionTypes((prev) => {
            const newMap = new Map(prev);
            newMap.delete(assistantId);
            return newMap;
          });
        }, 500);
      } catch (error) {
        // Revert optimistic update on error
        console.error("Assistant action failed:", error);

        if (action === "attach") {
          setWorkspaceAssistants((prev) =>
            prev.filter((a) => a.id !== assistantId)
          );
          setAvailableAssistants((prev) => [...prev, assistant]);
        } else if (action === "detach") {
          setAvailableAssistants((prev) =>
            prev.filter((a) => a.id !== assistantId)
          );
          setWorkspaceAssistants((prev) => [...prev, assistant]);
        }

        // Remove from transitioning
        setTransitioningAssistants((prev) => {
          const newSet = new Set(prev);
          newSet.delete(assistantId);
          return newSet;
        });
        setTransitionTypes((prev) => {
          const newMap = new Map(prev);
          newMap.delete(assistantId);
          return newMap;
        });
      }
    },
    [workspaceAssistants, availableAssistants, onAssistantAction]
  );

  if (isLoading || assistantsLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-[90vw] max-h-[90vh] w-[800px] h-[600px] bg-white dark:bg-zinc-900 border-[#3a3a3a] text-white p-0 gap-0 rounded-2xl flex flex-col overflow-hidden"
          style={{ zIndex: 9999 }}
          forceMount
          hideCloseButton={true}
        >
          <DialogTitle className="sr-only">
            Manage Project Assistants
          </DialogTitle>
          <DialogDescription className="sr-only">
            Attach or detach assistants to this workspace. Click the settings
            icon to configure each assistant.
          </DialogDescription>

          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 flex-shrink-0 rounded-t-2xl">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Manage Project Assistants
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onOpenChange(false)}
                className="h-8 w-8 bg-transparent hover:bg-transparent text-gray-900 dark:text-white hover:text-gray-900 dark:hover:text-white border-none"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-zinc-900 rounded-b-2xl">
            <div className="flex-1 overflow-auto">
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[90vw] max-h-[90vh] w-[800px] h-[600px] bg-white dark:bg-zinc-900 border-[#3a3a3a] text-white p-0 gap-0 rounded-2xl flex flex-col overflow-hidden"
        style={{ zIndex: 9999 }}
        forceMount
        hideCloseButton={true}
      >
        <DialogTitle className="sr-only">Manage Project Assistants</DialogTitle>
        <DialogDescription className="sr-only">
          Attach or detach assistants to this workspace. Click the settings icon
          to configure each assistant.
        </DialogDescription>

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 flex-shrink-0 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Manage Project Assistants
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              className="h-8 w-8 bg-transparent hover:bg-transparent text-gray-900 dark:text-white hover:text-gray-900 dark:hover:text-white border-none"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-zinc-900 rounded-b-2xl">
          <div className="flex-1 overflow-auto">
            <div className="p-4">
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
                Attach or detach assistants to this workspace. Click the
                settings icon to configure each assistant.
              </p>

              <div className="space-y-6">
                {/* Current Project Assistants Section */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-zinc-300 mb-4 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-violet-500"></span>
                    Current Project Assistants
                    {workspaceAssistants.length > 0 && (
                      <span className="text-xs text-gray-500 dark:text-zinc-500 bg-gray-100 dark:bg-zinc-800 px-2 py-1 rounded-full">
                        {workspaceAssistants.length}
                      </span>
                    )}
                  </h3>
                  {workspaceAssistants.length > 0 ? (
                    <div className="grid grid-cols-1 gap-3 mb-6">
                      {workspaceAssistants.map((assistant) => (
                        <AssistantCard
                          key={assistant.id}
                          assistant={assistant}
                          isWorkspaceAssistant={true}
                          isExpanded={expandedAssistantId === assistant.id}
                          onSettingsClick={handleSettingsClick}
                          onAssistantAction={handleAssistantAction}
                          isTransitioning={transitioningAssistants.has(
                            assistant.id
                          )}
                          transitionType={transitionTypes.get(assistant.id)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-gray-500 dark:text-zinc-500 text-sm py-6 px-4 text-center mb-6 bg-gray-50/50 dark:bg-zinc-800/30 rounded-xl border border-gray-200 dark:border-zinc-700">
                      No assistants attached to this project
                    </div>
                  )}
                </div>

                {/* Separator */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300 dark:border-zinc-600"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-3 bg-white dark:bg-zinc-900 text-gray-500 dark:text-zinc-400">
                      Available Assistants
                    </span>
                  </div>
                </div>

                {/* Available Assistants Section */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-zinc-300 mb-4 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-cyan-500"></span>
                    Available Assistants
                    {availableAssistants.length > 0 && (
                      <span className="text-xs text-gray-500 dark:text-zinc-500 bg-gray-100 dark:bg-zinc-800 px-2 py-1 rounded-full">
                        {availableAssistants.length}
                      </span>
                    )}
                  </h3>
                  {availableAssistants.length > 0 ? (
                    <div className="grid grid-cols-1 gap-3">
                      {availableAssistants.map((assistant) => (
                        <AssistantCard
                          key={assistant.id}
                          assistant={assistant}
                          isWorkspaceAssistant={false}
                          isExpanded={expandedAssistantId === assistant.id}
                          onSettingsClick={handleSettingsClick}
                          onAssistantAction={handleAssistantAction}
                          isTransitioning={transitioningAssistants.has(
                            assistant.id
                          )}
                          transitionType={transitionTypes.get(assistant.id)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-gray-500 dark:text-zinc-500 text-sm py-6 px-4 text-center bg-gray-50/50 dark:bg-zinc-800/30 rounded-xl border border-gray-200 dark:border-zinc-700">
                      No additional assistants available
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

AssistantCard.propTypes = {
  assistant: PropTypes.object.isRequired,
  isWorkspaceAssistant: PropTypes.bool.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onSettingsClick: PropTypes.func.isRequired,
  onAssistantAction: PropTypes.func.isRequired,
  onSavingStateChange: PropTypes.func,
  isTransitioning: PropTypes.bool,
  transitionType: PropTypes.string,
};

AssistantManagementDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  workspaceId: PropTypes.string.isRequired,
  onAssistantAction: PropTypes.func.isRequired,
  onSettingsClick: PropTypes.func,
};

export default AssistantManagementDialog;
